/**
 * Session-wide ClikArt placement tracking for the gallery's "Placed" badge, per spread:
 * artId -> set of spreadIds where an instance of that art currently sits.
 *
 * Tracked per spread (not a single flat boolean) so an art stays "Placed" until its LAST instance
 * is gone from every spread, and so removing one copy of an art that is still placed elsewhere
 * never clears the badge. Mirrors `lib/photos/photo-placement-session`.
 *
 * Two sources feed it. The live canvas (`reconcileClikArtPlacements`) is authoritative for the
 * spreads this session has opened. The backend's book-wide scan
 * (`seedClikArtPlacementsFromServer`) fills in every other spread, and once it has, an art the
 * book doesn't list counts as not placed instead of falling back to the catalog's own `placedFlag`.
 */
const placementsBySpread = new Map<string, Set<string>>()

/** Arts this session knows a placement history for — seen on a spread, or listed by the scan.
 *  Only these get an explicit `false` once all their instances are gone. */
const sessionTrackedArtIds = new Set<string>()

/** Spreads this session has reconciled from the live canvas; a scan never overwrites these. */
const reconciledSpreadIds = new Set<string>()

/** Spreads the last scan listed, so a later scan can clear the ones it no longer does. */
let seededSpreadIds = new Set<string>()

/** True once a finished scan has been applied. From then on, an art with no recorded placement is
 *  "not placed" rather than whatever the catalog's `placedFlag` says (that flag isn't per book). */
let isSeeded = false

/** Fired whenever the live canvas changes the session's ClikArt placements. A scan being applied
 *  does not fire it. */
export const CLIKART_PLACEMENT_CHANGED_EVENT = 'clikart-placement-changed'

const listeners = new Set<() => void>()

/** Immutable snapshot for `useSyncExternalStore` — changes reference whenever placements change. */
let overridesSnapshot: ReadonlyMap<string, boolean> = new Map()

function emitChange(notifyPlacementChanged = true): void {
  const flat = new Map<string, boolean>()

  for (const artId of sessionTrackedArtIds) {
    const spreadIds = placementsBySpread.get(artId)

    flat.set(artId, !!spreadIds && spreadIds.size > 0)
  }

  overridesSnapshot = flat

  for (const listener of listeners) {
    listener()
  }

  if (notifyPlacementChanged) {
    window.dispatchEvent(new CustomEvent(CLIKART_PLACEMENT_CHANGED_EVENT))
  }
}

export function subscribeClikArtPlacementOverrides(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getClikArtPlacementOverridesSnapshot(): ReadonlyMap<string, boolean> {
  return overridesSnapshot
}

/** Whether a finished scan has been applied to this session's placements. */
export function isClikArtPlacementSeeded(): boolean {
  return isSeeded
}

/** Makes `spreadId`'s slice of the tracking exactly `onSpread`. Returns whether anything changed. */
function setSpreadSlice(onSpread: ReadonlySet<string>, spreadId: string): boolean {
  let changed = false

  for (const artId of onSpread) {
    const spreadIds = placementsBySpread.get(artId)

    if (!sessionTrackedArtIds.has(artId)) {
      sessionTrackedArtIds.add(artId)
      changed = true
    }

    if (!spreadIds) {
      placementsBySpread.set(artId, new Set([spreadId]))
      changed = true
    } else if (!spreadIds.has(spreadId)) {
      spreadIds.add(spreadId)
      changed = true
    }
  }

  for (const [artId, spreadIds] of placementsBySpread) {
    if (spreadIds.has(spreadId) && !onSpread.has(artId)) {
      spreadIds.delete(spreadId)
      if (spreadIds.size === 0) placementsBySpread.delete(artId)
      changed = true
    }
  }

  return changed
}

/**
 * Align `spreadId`'s slice of the tracking with the arts currently on it (add, remove, move,
 * fill, clear fill). Other spreads' placements are never touched. A spread reconciled here is
 * the live canvas's from then on — a later scan will not overwrite it.
 */
export function reconcileClikArtPlacements(onSpread: ReadonlySet<string>, spreadId: string): void {
  reconciledSpreadIds.add(spreadId)

  if (setSpreadSlice(onSpread, spreadId)) emitChange()
}

/**
 * Apply the backend's book-wide scan: `placedBySpread` maps each spreadId to the ClikArt ids on
 * it. Spreads this session has already reconciled from the live canvas keep their live data;
 * every other spread takes the scan's, and one the scan no longer lists is cleared. Call it only
 * with a scan that has finished at least once — an empty result from a book that was never
 * scanned would otherwise read as "nothing is placed".
 */
export function seedClikArtPlacementsFromServer(
  placedBySpread: Readonly<Record<string, readonly string[]>>
): void {
  let changed = !isSeeded

  isSeeded = true

  const listedSpreadIds = new Set(Object.keys(placedBySpread))

  for (const spreadId of new Set([...seededSpreadIds, ...listedSpreadIds])) {
    if (reconciledSpreadIds.has(spreadId)) continue

    if (setSpreadSlice(new Set(placedBySpread[spreadId] ?? []), spreadId)) changed = true
  }

  seededSpreadIds = listedSpreadIds

  if (changed) emitChange(false)
}

/** Called at logout — a fresh login gets a fresh scan of its own book. */
export function clearClikArtPlacementOverrides(): void {
  if (placementsBySpread.size === 0 && sessionTrackedArtIds.size === 0 && !isSeeded) return

  placementsBySpread.clear()
  sessionTrackedArtIds.clear()
  reconciledSpreadIds.clear()
  seededSpreadIds = new Set()
  isSeeded = false
  emitChange()
}

/**
 * Whether one art counts as placed: this session's record if it has one; otherwise "not placed"
 * once a scan has been applied (the book is then fully known), or the catalog's server flag
 * before that. This is the one place the rule lives — the gallery's badge and filter and the
 * left panel's "Placed" row all ask it.
 *
 * Pass `undefined` as the server flag when the catalog has no entry for the art: with no session
 * record and no finished scan either, nothing is known yet and the answer is `undefined`.
 */
export function resolveClikArtPlaced<ServerFlag extends boolean | undefined>(
  artId: string,
  serverFlag: ServerFlag,
  overrides: ReadonlyMap<string, boolean> = getClikArtPlacementOverridesSnapshot()
): boolean | Extract<ServerFlag, undefined> {
  const override = overrides.get(artId)

  if (override !== undefined) return override

  // The result is `undefined` only when the caller passed an `undefined` flag (typed above).
  return (isSeeded ? false : serverFlag) as boolean | Extract<ServerFlag, undefined>
}

/** Overlay the session's placements onto gallery items (see `resolveClikArtPlaced`). */
export function applyClikArtPlacementOverrides<T extends { artId: string; placedFlag: boolean }>(
  items: T[],
  overrides: ReadonlyMap<string, boolean> = getClikArtPlacementOverridesSnapshot()
): T[] {
  if (overrides.size === 0 && !isSeeded) return items

  return items.map((item) => {
    const placedFlag = resolveClikArtPlaced(item.artId, item.placedFlag, overrides)

    return placedFlag === item.placedFlag ? item : { ...item, placedFlag }
  })
}
